import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, checkPaymentStatus, clearPaymentStatusCache } from '../services/supabase';
import logger from '../utils/logger';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paymentApproved, setPaymentApproved] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    let isSubscribed = true;

    const handleLogout = async () => {
        logger.auth('Iniciando processo de logout...');
        setLoading(true);
        try {
            await supabase.auth.signOut();
            setUser(null);
            setPaymentApproved(false);
            clearPaymentStatusCache();
            navigate('/', { replace: true });
        } catch (error) {
            logger.error('Erro durante logout:', error);
            // Em caso de erro, ainda tentamos limpar o estado
            setUser(null);
            setPaymentApproved(false);
            navigate('/', { replace: true });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        logger.auth('Iniciando useEffect do AuthContext');
        logger.auth('Rota atual:', location.pathname);
        
        const initSession = async () => {
            logger.auth('Iniciando initSession...');
            logger.auth('isSubscribed:', isSubscribed);
            
            try {
                const { data: { session } } = await supabase.auth.getSession();
                
                if (!isSubscribed) return;

                if (session?.user) {
                    logger.auth('Usuário encontrado:', session.user.email);
                    setUser(session.user);
                    
                    try {
                        logger.payment('Verificando status do pagamento...');
                        const paymentStatus = await checkPaymentStatus(session.user.id);
                        if (!isSubscribed) return;
                        
                        logger.payment('Status:', paymentStatus);
                        setPaymentApproved(paymentStatus === 'approved');

                        // Redireciona com base no status do pagamento e caminho atual
                        const currentPath = location.pathname;
                        logger.auth('Caminho atual:', currentPath);

                        if (paymentStatus === 'approved') {
                            if (currentPath === '/payment') {
                                logger.auth('Redirecionando para dashboard...');
                                navigate('/dashboard', { replace: true });
                            }
                        } else {
                            if (currentPath !== '/payment' && currentPath !== '/') {
                                logger.auth('Redirecionando para payment...');
                                navigate('/payment', { replace: true });
                            }
                        }
                    } catch (error) {
                        logger.error('Erro ao verificar pagamento:', error);
                        if (location.pathname !== '/payment' && location.pathname !== '/') {
                            navigate('/payment', { replace: true });
                        }
                    }
                } else {
                    logger.auth('Nenhum usuário encontrado');
                    setUser(null);
                    setPaymentApproved(false);
                    if (location.pathname !== '/') {
                        logger.auth('Redirecionando para home...');
                        navigate('/', { replace: true });
                    }
                }
            } catch (error) {
                logger.error('Erro ao inicializar sessão:', error);
                if (isSubscribed) {
                    await navigate('/', { replace: true });
                }
            } finally {
                if (isSubscribed) {
                    logger.auth('Finalizando inicialização');
                    setLoading(false);
                }
            }
        };

        // Inscreve-se nos eventos de autenticação
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
            logger.auth('Evento de autenticação:', event);
            
            // Apenas processa eventos SIGNED_IN e SIGNED_OUT
            if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
                await initSession();
            }
        });

        // Inicializa a sessão
        initSession();

        return () => {
            logger.auth('Limpando efeito do AuthContext');
            logger.auth('Estado final - isSubscribed:', isSubscribed);
            isSubscribed = false;
            subscription.unsubscribe();
        };
    }, [navigate, location.pathname]);

    if (loading) {
        return <div>Carregando...</div>;
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                paymentApproved,
                signOut: handleLogout,
                setUser,
                setLoading,
                setPaymentApproved
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth deve ser usado dentro de um AuthProvider');
    }
    return context;
};
